import { useEffect, useRef } from "react";
import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import { WebLinksAddon } from "@xterm/addon-web-links";
import "@xterm/xterm/css/xterm.css";

interface ConsoleOutputProps {
  lines: string[];
  onInput?: (data: string) => void;
  onResize?: (cols: number, rows: number) => void;
}

export function ConsoleOutput({ lines, onInput, onResize }: ConsoleOutputProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const termRef = useRef<Terminal | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const writtenCountRef = useRef(0);

  useEffect(() => {
    if (!containerRef.current) return;

    const terminal = new Terminal({
      theme: {
        background: "#1a1a2e",
        foreground: "#e0e0e0",
        cursor: "#4ade80",
        selectionBackground: "#4ade8040",
        black: "#1a1a2e",
        red: "#f87171",
        green: "#4ade80",
        yellow: "#facc15",
        blue: "#60a5fa",
        magenta: "#c084fc",
        cyan: "#22d3ee",
        white: "#e0e0e0",
        brightBlack: "#6b7280",
        brightRed: "#fca5a5",
        brightGreen: "#86efac",
        brightYellow: "#fde047",
        brightBlue: "#93c5fd",
        brightMagenta: "#d8b4fe",
        brightCyan: "#67e8f9",
        brightWhite: "#ffffff",
      },
      fontSize: 13,
      fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', Menlo, Monaco, monospace",
      cursorBlink: true,
      cursorStyle: "bar",
      scrollback: 10000,
      convertEol: true,
      allowProposedApi: true,
    });

    const fitAddon = new FitAddon();
    terminal.loadAddon(fitAddon);
    terminal.loadAddon(new WebLinksAddon());

    terminal.open(containerRef.current);
    fitAddon.fit();
    onResize?.(terminal.cols, terminal.rows);

    if (onInput) {
      terminal.onData(onInput);
    }

    termRef.current = terminal;
    fitAddonRef.current = fitAddon;
    writtenCountRef.current = 0;

    const resizeObserver = new ResizeObserver(() => {
      fitAddon.fit();
      onResize?.(terminal.cols, terminal.rows);
    });
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      terminal.dispose();
      termRef.current = null;
      fitAddonRef.current = null;
      writtenCountRef.current = 0;
    };
  }, [onInput, onResize]);

  useEffect(() => {
    const term = termRef.current;
    if (!term) return;

    for (let i = writtenCountRef.current; i < lines.length; i++) {
      term.write(lines[i]);
    }
    writtenCountRef.current = lines.length;
  }, [lines]);

  return (
    <div
      ref={containerRef}
      className="flex-1 min-h-0"
    />
  );
}
